import type { ReactNode } from 'react';
import { Handle } from '@xyflow/react';
import type { FlowNodeType } from '@reflex/flows';
import { cn } from '@reflex/ui/lib/utils';
import {
  flowSourceHandlePosition,
  flowTargetHandlePosition,
  useFlowLayoutDirection,
} from './flow-layout-direction-context';
import { FlowNodeTag } from './FlowNodeTag';
import { FLOW_NODE_SIZE, FLOW_NODE_SIZE_CLASS, FLOW_NODE_THEME } from './flow-node-theme';

interface FlowNodeBaseProps {
  nodeType: FlowNodeType;
  label: string;
  /** Extra qualifier in the type pill, e.g. Trigger · Event. */
  tagSubtype?: string;
  description?: ReactNode;
  footer?: ReactNode;
  selected?: boolean;
  /** Running / waiting: type-colored border when not selected. */
  isActive?: boolean;
  status?: string;
  extraClassName?: string;
  children?: ReactNode;
  sourceHandles?: ReactNode;
  hideTargetHandle?: boolean;
  testId?: string;
}

export function FlowNodeBase({
  nodeType,
  label,
  tagSubtype,
  description,
  footer,
  selected,
  isActive,
  status,
  extraClassName,
  children,
  sourceHandles,
  hideTargetHandle,
  testId = 'flow-node-card',
}: FlowNodeBaseProps) {
  const theme = FLOW_NODE_THEME[nodeType];
  const size = FLOW_NODE_SIZE[nodeType];
  const direction = useFlowLayoutDirection();
  const targetPosition = flowTargetHandlePosition(direction);
  const sourcePosition = flowSourceHandlePosition(direction);

  return (
    <div
      className={cn(
        'flex min-h-[73px] flex-col gap-2 overflow-hidden rounded-lg border bg-card p-3',
        FLOW_NODE_SIZE_CLASS[size],
        selected
          ? theme.selected
          : cn(
              'border-[#e5e5e5] shadow-[0px_6px_16px_0px_rgba(0,0,0,0.08)] dark:border-border',
              theme.hover,
              isActive && theme.active,
            ),
        extraClassName,
      )}
      data-testid={testId}
      data-node-type={nodeType}
      data-status={status}
      data-selected={selected ? 'true' : 'false'}
    >
      {hideTargetHandle ? null : (
        <Handle type="target" position={targetPosition} className="bg-muted-foreground!" />
      )}
      <div className="flex min-w-0 flex-col items-start gap-2">
        <FlowNodeTag type={nodeType} subtype={tagSubtype} />
        <p className="truncate text-sm font-semibold leading-[1.3] text-foreground">{label}</p>
      </div>
      {description ? (
        <div className="min-w-0 text-xs leading-[1.3] text-muted-foreground">{description}</div>
      ) : null}
      {children}
      {footer ? (
        <div className="min-w-0 text-xs leading-[1.3] text-muted-foreground">{footer}</div>
      ) : null}
      {sourceHandles ?? (
        <Handle type="source" position={sourcePosition} className="bg-muted-foreground!" />
      )}
    </div>
  );
}

/** Gray mono pills for router branches and approval decisions. */
export function FlowNodePills({ labels }: { labels: readonly string[] }) {
  if (labels.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {labels.map((label) => (
        <span
          key={label}
          className="rounded bg-muted px-1 py-1 font-mono text-[11px] font-medium leading-3 text-muted-foreground"
        >
          {label}
        </span>
      ))}
    </div>
  );
}
